<template>
  <div class="flex flex-col items-center justify-center p-4 bg-white rounded-lg shadow-lg min-w-[240px] mx-2 mb-4">
    <div class="flex items-center w-full mb-3">
      <div 
        class="w-10 h-10 rounded-full mr-3 flex items-center justify-center text-white font-semibold text-sm"
        :style="{ backgroundColor: avatarColor }"
      >
        {{ initials }}
      </div>
      <div class="flex-1">
        <h3 class="text-sm font-semibold text-gray-900">{{ creatorName }}</h3>
        <p class="text-xs text-gray-600">@{{ username }}</p>
      </div>
    </div>
    
    <div class="w-full bg-gray-accent rounded-md p-3">
      <div class="flex justify-between items-center mb-2">
        <span class="text-xs font-semibold text-gray-700">{{ getTranslation('monthlyRevenue') }}</span>
        <span class="text-sm font-bold text-gray-900">€{{ monthlyRevenue.toLocaleString() }}</span>
      </div>
      
      <div class="flex justify-between items-center mb-2">
        <span class="text-xs font-semibold text-red-700 leading-tight">{{ getTranslation('potentialLoss') }}</span>
        <span class="text-sm font-bold text-red-600">-€{{ potentialLoss.toLocaleString() }}</span>
      </div>
      
      <!-- Saving Potential (Row 1) -->
      <div v-if="cardType === 'potential'" class="border-t pt-2 mt-2 text-center">
        <div class="bg-yellow-50 rounded-md p-2">
          <p class="text-xs font-semibold text-yellow-700 mb-1">{{ getTranslation('savingPotential') }}</p>
          <p class="text-lg font-bold text-yellow-600">{{ savingPotential }}%</p>
          <p class="text-xs text-gray-600">{{ getTranslation('preventable') }}</p>
        </div>
      </div>
      
      <!-- Amount Saved (Row 2) -->
      <div v-if="cardType === 'saved'" class="border-t pt-2 mt-2 text-center">
        <div class="bg-green-50 rounded-md p-2">
          <p class="text-xs font-semibold text-green-700 mb-1">{{ getTranslation('amountSaved') }}</p>
          <p class="text-lg font-bold text-green-600">€{{ amountSaved.toLocaleString() }}</p>
          <p class="text-xs text-gray-600">{{ savingPercentage }}% {{ getTranslation('saved') }}</p>
        </div>
      </div>
    </div>
    
    <!-- Protected Badge (only for saved cards) - Half on/off card -->
    <div v-if="cardType === 'saved'" class="relative w-full flex justify-center">
      <div class="absolute -bottom-4 inline-flex items-center px-3 py-2 rounded-full text-xs font-semibold bg-black text-white shadow-lg z-10">
        <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
        </svg>
        {{ getTranslation('protected') }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  creatorName: {
    type: String,
    required: true
  },
  username: {
    type: String,
    required: true
  },
  monthlyRevenue: {
    type: Number,
    required: true
  },
  potentialLoss: {
    type: Number,
    required: true
  },
  cardType: {
    type: String,
    required: true,
    validator: value => ['potential', 'saved'].includes(value)
  },
  savingPotential: {
    type: Number,
    default: 0
  },
  amountSaved: {
    type: Number,
    default: 0
  },
  savingPercentage: {
    type: Number,
    default: 0
  },
  locale: {
    type: String,
    default: 'en'
  }
});

// Generate initials from creator name
const initials = computed(() => {
  return props.creatorName.split(' ').map(name => name[0]).join('').toUpperCase();
});

// Generate a consistent color based on the creator name
const avatarColor = computed(() => {
  const colors = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
    '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6366F1'
  ];
  let hash = 0;
  for (let i = 0; i < props.creatorName.length; i++) {
    hash = props.creatorName.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
});

// Translation function
const getTranslation = (key) => {
  const translations = {
    en: {
      monthlyRevenue: 'Monthly Revenue',
      potentialLoss: 'Potential Loss to Leaks',
      savingPotential: 'Saving Potential',
      preventable: 'preventable with Revrs',
      amountSaved: 'Amount Saved',
      saved: 'saved',
      protected: 'Protected by Revrs'
    },
    de: {
      monthlyRevenue: 'Monatlicher Umsatz',
      potentialLoss: 'Verlust durch Leaks',
      savingPotential: 'Einsparpotenzial',
      preventable: 'vermeidbar mit Revrs',
      amountSaved: 'Gesparter Betrag',
      saved: 'gespart',
      protected: 'Geschützt von Revrs'
    }
  };
  
  return translations[props.locale]?.[key] || translations.en[key];
};
</script>